import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Row,
  Button,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { apiService } from '../../constants/ApiService';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';

const StudentHostelAssign = () => {
  const { user } = useSelector((s) => s.authState);
  const navigate = useNavigate();
  const location = useLocation();

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});

  const [editId, setEditId] = useState(0);

  // Enrollment dropdown
  const [enrollmentRecords, setEnrollmentRecords] = useState([]);
  const [autoCompleteValue, setAutoCompleteValue] = useState(null);

  // Hostel dropdown
  const [hostelRecords, setHostelRecords] = useState([]);
  const [hostelAutoValue, setHostelAutoValue] = useState(null);

  const [data, setData] = useState({
    vendor_id: 0,
    branch_id: 0,

    student_id: '',
    enrollment_id: '',

    student_name: '',
    admit_no: '',
    enrollment_number: '',

    hostel_id: '',
    hostel_name: '',

    building_name: '',
    room_name: '',
    detail: '',
    from_date: '',
    to_date: '',
  });

  // ---------------------------
  // Enrollment Search API
  // ---------------------------
  const GetEnrollmentForDropdown = async (search, selectedEnrollmentNoOrId) => {
    try {
      const canLoad = conditionalDataLoad({
        hasMultiBranch,
        hasSingleBranch,
        isSuperAdmin,
        isAdmin,
        vendorId: data.vendor_id,
        branchId: data.branch_id,
      });

      if (!canLoad) return;

      const response = await apiService.get(
        `api/Enrollment/GetAllEnrollment?vendorId=${data.vendor_id}&branchId=${data.branch_id}` +
          `&search=${encodeURIComponent(search || '')}&classId=0&sessionYear=`,
      );

      if (response?.status === true) {
        const list = response.data || [];
        setEnrollmentRecords(list);

        const key = selectedEnrollmentNoOrId || search;
        if (key) {
          const match =
            list.find((x) => String(x.enrollment_number) === String(key)) ||
            list.find((x) => String(x.enrollment_id) === String(key));

          if (match) setAutoCompleteValue(match);
        }
      } else {
        setEnrollmentRecords([]);
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to load students');
    }
  };

  const GetHostelForDropdown = async (searchText = '') => {
    try {
      const vendorId = data.vendor_id || '';
      const branchId = data.branch_id || '';

      const res = await apiService.get(
        `api/HostelMaster/GetAllHostel?vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const list = res.data || [];
        setHostelRecords(list);

        // ✅ if editing, try match hostelId and set selected
        if (data.hostel_id) {
          const match = list.find((h) => String(h.id) === String(data.hostel_id));
          if (match) setHostelAutoValue(match);
        }
      } else {
        setHostelRecords([]);
      }
    } catch (err) {
      console.error(err);
      setHostelRecords([]);
    }
  };

  // ---------------------------
  // Edit load from List (location.state)
  // ---------------------------
  useEffect(() => {
    if (location?.state?.id) {
      const row = location.state;
      setEditId(Number(row.id || 0));

      setData((prev) => ({
        ...prev,
        vendor_id: row.vendor_id || prev.vendor_id,
        branch_id: row.branch_id || prev.branch_id,

        student_id: row.student_id ? String(row.student_id) : '',
        enrollment_id: row.enrollment_id ? String(row.enrollment_id) : '',

        hostel_id: row.hostel_id ? String(row.hostel_id) : '',
        building_name: row.building_name || '',
        room_name: row.room_name || '',
        detail: row.detail || '',
        from_date: row.from_date ? String(row.from_date).slice(0, 10) : '',
        to_date: row.to_date ? String(row.to_date).slice(0, 10) : '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // vendor/branch change -> load dropdowns
  useEffect(() => {
    setEnrollmentRecords([]);
    setHostelRecords([]);

    if (!data.vendor_id || !data.branch_id) return;

    // load dropdowns
    GetEnrollmentForDropdown('', data.enrollment_id || '');
    GetHostelForDropdown('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.vendor_id, data.branch_id]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setData((p) => ({ ...p, [name]: value }));
    setErrorMessage((p) => ({ ...p, [name]: '' }));
  };

  const onStudentInputChange = (event, newInputValue) => {
    if (isSuperAdmin && (!data.vendor_id || Number(data.vendor_id) <= 0)) {
      toast.error('Please select a Vendor');
      return;
    }
    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!data.branch_id || Number(data.branch_id) <= 0)
    ) {
      toast.error('Please select a Branch');
      return;
    }
    GetEnrollmentForDropdown(newInputValue, newInputValue);
  };

  const onStudentChange = (event, newValue) => {
    if (newValue) {
      setAutoCompleteValue(newValue);
      setData((prev) => ({
        ...prev,
        enrollment_id: newValue.enrollment_id
          ? String(newValue.enrollment_id)
          : '',
        student_id: newValue.student_id ? String(newValue.student_id) : '',
        student_name: newValue.student_name || '',
        admit_no: newValue.admit_no || '',
        enrollment_number: newValue.enrollment_number || '',
      }));
    } else {
      setAutoCompleteValue(null);
      setData((prev) => ({
        ...prev,
        student_id: '',
        enrollment_id: '',
        student_name: '',
        admit_no: '',
        enrollment_number: '',
      }));
    }
  };

  const onHostelInputChange = (event, newInputValue) => {
    if (isSuperAdmin && (!data.vendor_id || Number(data.vendor_id) <= 0)) {
      toast.error('Please select a Vendor');
      return;
    }
    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!data.branch_id || Number(data.branch_id) <= 0)
    ) {
      toast.error('Please select a Branch');
      return;
    }
    GetHostelForDropdown(newInputValue);
  };

  const onHostelChange = (event, newValue) => {
    if (newValue) {
      setHostelAutoValue(newValue);
      setData((prev) => ({
        ...prev,
        hostel_id: newValue.id ? String(newValue.id) : '',
        hostel_name: newValue.hostel_name || '',
      }));
    } else {
      setHostelAutoValue(null);
      setData((prev) => ({
        ...prev,
        hostel_id: '',
        hostel_name: '',
      }));
    }
  };

  const validate = () => {
    const err = {};

    if (isSuperAdmin && (!data.vendor_id || Number(data.vendor_id) <= 0)) {
      err.vendor_id = 'Please select a Vendor';
    }
    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!data.branch_id || Number(data.branch_id) <= 0)
    ) {
      err.branch_id = 'Please select a Branch';
    }

    if (!data.enrollment_id)
      err.enrollment_id = 'Please select student (enrollment)';
    if (!data.student_id) err.student_id = 'Student not found';

    if (!data.hostel_id) err.hostel_id = 'Hostel is required';
    if (!data.from_date) err.from_date = 'From date is required';

    setErrorMessage(err);
    return Object.keys(err).length === 0;
  };

  const save = async () => {
    if (!validate()) {
      toast.error('Please fill required fields');
      return;
    }

    const payload = {
      ...data,
      vendor_id: data.vendor_id ? Number(data.vendor_id) : 0,
      branch_id: data.branch_id ? Number(data.branch_id) : 0,
      student_id: data.student_id ? Number(data.student_id) : 0,
      enrollment_id: data.enrollment_id ? Number(data.enrollment_id) : 0,
      hostel_id: data.hostel_id ? Number(data.hostel_id) : 0,
    };

    let res;

    if (editId && editId > 0) {
      res = await apiService.put(
        `api/HostelMaster/UpdateHostelMapping?id=${editId}`,
        payload,
      );
    } else {
      res = await apiService.post('api/HostelMaster/AssignHostel', payload);
    }

    if (res?.status) {
      toast.success(res.message);
      navigate('/list-hostel-mapping');
    } else {
      toast.error(res?.message || 'Failed');
    }
  };

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={data}
        setSelectionData={setData}
        errorMessage={errorMessage}
        accessButton
      />

      <Card className='card-box pd-20'>
        <CardTitle className='p-3 border-bottom d-flex justify-content-between align-items-center'>
          <span>{editId ? 'Edit Hostel Mapping' : 'Assign Hostel to Student'}</span>
          <Button color='warning' onClick={() => navigate(-1)}>
            <i className='bi bi-arrow-left' />
          </Button>
        </CardTitle>

        <CardBody>
          <Row>
            <FormGroup className='col-sm-6'>
              <Label>Student (Search Enrollment)</Label>

              <Autocomplete
                id='enrollment_student'
                options={enrollmentRecords}
                value={autoCompleteValue}
                getOptionLabel={(option) =>
                  (option.enrollment_number || '') +
                  ' - ' +
                  (option.admit_no || '') +
                  ' - ' +
                  (option.student_name || '')
                }
                onChange={onStudentChange}
                onInputChange={onStudentInputChange}
                openOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Search Enrollment No / Admit No / Student Name...'
                    variant='outlined'
                    error={
                      !!errorMessage.enrollment_id || !!errorMessage.student_id
                    }
                    helperText={
                      errorMessage.enrollment_id ||
                      errorMessage.student_id ||
                      ''
                    }
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '36px',
                        fontSize: '14px',
                        borderRadius: '4px',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px',
                      },
                    }}
                  />
                )}
              />
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>Hostel</Label>

              <Autocomplete
                id='hostel_id'
                options={hostelRecords}
                value={hostelAutoValue}
                getOptionLabel={(option) =>
                  (option.hostel_name || '') +
                  (option.hostel_code ? ` (${option.hostel_code})` : '')
                }
                onChange={onHostelChange}
                onInputChange={onHostelInputChange}
                openOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder='Search Hostel...'
                    variant='outlined'
                    error={!!errorMessage.hostel_id}
                    helperText={errorMessage.hostel_id || ''}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '36px',
                        fontSize: '14px',
                        borderRadius: '4px',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px',
                      },
                    }}
                  />
                )}
              />
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>Building</Label>
              <Input
                name='building_name'
                value={data.building_name}
                onChange={onChange}
              />
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>Room</Label>
              <Input name='room_name' value={data.room_name} onChange={onChange} />
            </FormGroup>

            <FormGroup className='col-sm-12'>
              <Label>Detail</Label>
              <Input name='detail' value={data.detail} onChange={onChange} />
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>From Date</Label>
              <Input
                type='date'
                name='from_date'
                value={data.from_date}
                onChange={onChange}
                invalid={!!errorMessage.from_date}
              />
              {errorMessage.from_date ? (
                <small className='text-danger'>{errorMessage.from_date}</small>
              ) : null}
            </FormGroup>

            <FormGroup className='col-sm-6'>
              <Label>To Date</Label>
              <Input
                type='date'
                name='to_date'
                value={data.to_date}
                onChange={onChange}
              />
            </FormGroup>
          </Row>

          <Button color='primary' className='float-end' onClick={save}>
            {editId ? 'Update Mapping' : 'Assign Hostel'}
          </Button>
        </CardBody>
      </Card>
    </>
  );
};

export default StudentHostelAssign;
